import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    useCompleteWizard,
    useSaveWizardState,
    useWizardStateQuery,
} from '../queries/businessProfileQueries';
import {
    WIZARD_STEPS,
    WizardStepDefinition,
    WizardStepId,
    findStepById,
    findStepByPath,
    nextStep,
    previousStep,
    stepUrl,
} from './steps';

interface WizardNavigationOptions {
    flushStep: () => Promise<boolean>;
}

export function useWizardNavigation({ flushStep }: WizardNavigationOptions) {
    const navigate = useNavigate();
    const { step: stepPath } = useParams<{ step: string }>();

    const { data: wizardState, isLoading } = useWizardStateQuery();
    const { mutateAsync: saveWizardState } = useSaveWizardState();
    const { mutateAsync: completeWizard } = useCompleteWizard();

    const matchedStep = useMemo(() => findStepByPath(stepPath), [stepPath]);

    const currentStep: WizardStepDefinition = matchedStep ?? WIZARD_STEPS[0];

    // False for bare /business/wizard or an unknown step -> triggers a redirect
    const isCanonicalPath = !!matchedStep;

    // Where a bare /business/wizard should land: where the user left off, else the start
    const resumeStep = useMemo(
        () =>
            (wizardState?.lastVisitedStepId
                ? findStepById(wizardState.lastVisitedStepId as WizardStepId)
                : undefined) ?? WIZARD_STEPS[0],
        [wizardState],
    );

    const completedStepIds = useMemo(
        () => (wizardState?.completedStepIds ?? []) as WizardStepId[],
        [wizardState],
    );

    const goToStep = useCallback(
        async (step: WizardStepDefinition) => {
            if (!(await flushStep())) {
                return;
            }
            await saveWizardState({ lastVisitedStepId: step.id });
            navigate(stepUrl(step));
        },
        [flushStep, saveWizardState, navigate],
    );

    const completeAndAdvance = useCallback(async () => {
        const upcoming = nextStep(currentStep.id);
        const completed = Array.from(new Set([...completedStepIds, currentStep.id]));

        await saveWizardState({
            completedStepIds: completed,
            lastVisitedStepId: upcoming?.id ?? currentStep.id,
        });

        if (upcoming) {
            navigate(stepUrl(upcoming));
            return;
        }

        await completeWizard();
        navigate('/home');
    }, [currentStep.id, completedStepIds, saveWizardState, completeWizard, navigate]);

    const skip = useCallback(async () => {
        const upcoming = nextStep(currentStep.id);
        if (!upcoming) {
            return;
        }
        await goToStep(upcoming);
    }, [currentStep.id, goToStep]);

    const goBack = useCallback(async () => {
        const prior = previousStep(currentStep.id);
        if (prior) {
            await goToStep(prior);
        }
    }, [currentStep.id, goToStep]);

    const saveAndExit = useCallback(async () => {
        if (!(await flushStep())) {
            return;
        }
        await saveWizardState({ lastVisitedStepId: currentStep.id });
        navigate('/home');
    }, [flushStep, currentStep.id, saveWizardState, navigate]);

    return {
        currentStep,
        isCanonicalPath,
        resumeStep,
        completedStepIds,
        wizardState,
        isLoading,
        businessProfileId: wizardState?.businessProfileId ?? null,
        activeEntityId: wizardState?.activeEntityId ?? null,
        goToStep,
        goBack,
        skip,
        saveAndExit,
        completeAndAdvance,
        hasPrevious: !!previousStep(currentStep.id),
    };
}
